import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = { duration: 1000 };
const DEFAULT_SPEED_FACTOR = 100;
const MIN_SPEED_SECONDS = 8;

class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content'];

  connectedCallback() {
    super.connectedCallback();

    const { content } = this.refs;
    if (!content?.firstElementChild || content.firstElementChild.children.length === 0) return;

    this.#addRepeatedItems();
    this.#duplicateContent();
    this.#setSpeed();

    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
  }

  #animation = null;

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.#getAnimation();
    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => this.#setPlaybackRate(animation, value),
      onComplete: () => { this.#animation = null; },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp = () => {
    this.#slowDown.cancel?.();

    const animation = this.#getAnimation();
    if (!animation) return;

    const currentRate = this.#getPlaybackRate(animation);
    if (currentRate === 1) return;

    const from = this.#animation?.current ?? currentRate ?? 0;
    this.#animation?.cancel?.();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => this.#setPlaybackRate(animation, value),
      onComplete: () => { this.#animation = null; },
    });
  };

  #getAnimation() {
    return this.refs.wrapper?.getAnimations?.()[0] ?? null;
  }

  #getPlaybackRate(animation) {
    // CSSAnimation inherits from Animation; prefer .playbackRate
    return typeof animation.playbackRate === 'number' ? animation.playbackRate : 1;
  }

  #setPlaybackRate(animation, value) {
    if (typeof animation.updatePlaybackRate === 'function') {
      animation.updatePlaybackRate(value);
    } else {
      animation.playbackRate = value;
    }
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper?.lastElementChild ?? null;
    return content && lastChild && content !== lastChild ? lastChild : null;
  }

  #setSpeed(value = this.#calculateSpeed()) {
    const safe = Number.isFinite(value) ? Math.max(value, MIN_SPEED_SECONDS) : MIN_SPEED_SECONDS;
    this.style.setProperty('--marquee-speed', `${safe}s`);
  }

  #calculateSpeed() {
    const raw = Number(this.getAttribute('data-speed-factor'));
    const speedFactor = Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_SPEED_FACTOR;
    const marqueeWidth = this.offsetWidth || 1;
    const speed = Math.ceil(marqueeWidth / speedFactor / 2);
    return speed;
  }

  #handleResize = debounce(() => {
    const { content } = this.refs;
    if (!content?.firstElementChild) return;

    const newNumberOfCopies = this.#calculateNumberOfCopies();
    const currentNumberOfCopies = content.children.length;

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed();
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper?.getAnimations?.() ?? [];
    requestAnimationFrame(() => {
      for (const animation of animations) {
        try { animation.currentTime = 0; } catch {}
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const { content, wrapper } = this.refs;
    if (!content || !wrapper) return;

    const clone = /** @type {HTMLElement} */ (content.cloneNode(true));
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    wrapper.appendChild(clone);
  }

  #addRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content } = this.refs;
    const base = content?.firstElementChild;
    if (!content || !base) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = base.cloneNode(true);
      content.appendChild(clone);
    }
  }

  #removeRepeatedItems(numberToRemove) {
    const { content } = this.refs;
    if (!content || !Number.isFinite(numberToRemove) || numberToRemove <= 0) return;

    for (let i = 0; i < numberToRemove; i++) {
      // Avoid removing the original first child
      if (content.children.length <= 1) break;
      content.lastElementChild?.remove();
    }
  }

  #calculateNumberOfCopies() {
    const { content } = this.refs;
    const marqueeWidth = this.offsetWidth || 1;
    const first = content?.firstElementChild;
    const itemWidth = first instanceof HTMLElement ? (first.offsetWidth || 1) : 1;
    return Math.max(1, Math.ceil(marqueeWidth / itemWidth));
  }
}

/**
 * @param {{ from:number, to:number, duration:number, onUpdate:(n:number)=>void, easing?:(t:number)=>number, onComplete?:()=>void }} params
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  function step(now) {
    if (cancelled) return;
    const elapsed = now - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const eased = easing(progress);
    currentValue = from + (to - from) * eased;
    onUpdate(currentValue);
    if (progress < 1) {
      requestAnimationFrame(step);
    } else {
      onComplete?.();
    }
  }

  requestAnimationFrame(step);

  return {
    get current() { return currentValue; },
    cancel() { cancelled = true; },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
